import { VISUALIZATION_CONTENT_CONTAINER_CLASS_NAME } from '@ardoq/global-consts';
import { ChartWindowState, PanZoomClient, panAndZoom } from '@ardoq/graph';
import { useEffect, useRef } from 'react';
import { chartArea as ChartArea } from './chartArea';
import { noop } from 'lodash';
import { CidArgs } from '@ardoq/common-helpers';
import styled from 'styled-components';
import { XAxis } from '../xAxis/xAxis';
import {
  SetWindowFunction,
  TimelineChartAreaArgs,
  WindowScalingLimitsForGrid,
} from '../types';

const TimelineChartContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

type TimelineZoomContainerProps = ChartWindowState &
  Omit<TimelineChartAreaArgs, 'xAxisContainerHeight'> & {
    registerPanZoomClient: (client: PanZoomClient) => void;
    componentSingleClickHandler?: (modelId: string) => void;
    componentDoubleClickHandler?: (modelId: string) => void;
    setWindow: SetWindowFunction;
    isPanning: () => boolean;
    windowScalingLimitsForGrid: WindowScalingLimitsForGrid;
  };

const getPanAndZoomComponentClickHandler =
  (handler: (modelId: string) => void) => (args: CidArgs, e: MouseEvent) => {
    if (
      (e.target as HTMLElement).matches('.expander *') ||
      (e.target as HTMLDivElement).closest('.expander')
    ) {
      return;
    }
    handler(args.cid);
  };

const TimelineZoomContainerBase = (props: TimelineZoomContainerProps) => {
  const {
    registerPanZoomClient,
    isPanning,
    componentSingleClickHandler,
    componentDoubleClickHandler,
    timelineChartProperties: { dimensions, xAxisProps },
  } = props;

  const xAxis = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerPanZoomClient({
      getDimensions: () => dimensions,
      offsetToAxes: (e: WheelEvent) => [e.offsetX - dimensions.margin.left, 0],
      onWindowChanged: noop,
      getMouseWheelElement: () => xAxis.current!,
      selectComponent: componentSingleClickHandler
        ? getPanAndZoomComponentClickHandler(componentSingleClickHandler)
        : undefined,
      onComponentDoubleClick: componentDoubleClickHandler
        ? getPanAndZoomComponentClickHandler(componentDoubleClickHandler)
        : undefined,
    });
  }, [
    dimensions,
    registerPanZoomClient,
    componentSingleClickHandler,
    componentDoubleClickHandler,
  ]);

  return (
    <TimelineChartContainer
      className={VISUALIZATION_CONTENT_CONTAINER_CLASS_NAME}
    >
      <XAxis ref={xAxis} xAxisData={xAxisProps} dimensions={dimensions} />
      <ChartArea
        {...props}
        componentSingleClickHandler={componentSingleClickHandler}
        componentDoubleClickHandler={componentDoubleClickHandler}
        isPanning={isPanning()}
      />
    </TimelineChartContainer>
  );
};

export const TimelineZoomContainer = panAndZoom(TimelineZoomContainerBase);
